import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { User, ArrowLeft, ClipboardList, TrendingUp, BookOpen, BarChart2, Loader2, Trash2, Eye } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useSupabaseData } from '@/contexts/SupabaseDataContext';
import EvaluationDetailsModal from '@/components/evaluation/EvaluationDetailsModal';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const StudentDashboard = ({ student, onNavigate }) => {
  const { fetchEvaluationsByStudent, fetchClassLogsByStudent, deleteStudent, deleteEvaluation, deleteClassLog } = useSupabaseData();
  const [evaluations, setEvaluations] = useState([]);
  const [evolutionLogs, setEvolutionLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvaluation, setSelectedEvaluation] = useState(null);
  const [isEvaluationModalOpen, setIsEvaluationModalOpen] = useState(false);

  const loadData = async () => {
    if (student) {
      setLoading(true);
      const studentEvaluations = await fetchEvaluationsByStudent(student.id);
      const studentLogs = await fetchClassLogsByStudent(student.id);
      setEvaluations(studentEvaluations);
      setEvolutionLogs(studentLogs);
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [student]);

  if (!student) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-white text-xl">Aluno não encontrado. Por favor, volte ao dashboard.</p>
        <Button onClick={() => onNavigate('dashboard')} className="ml-4">Voltar</Button>
      </div>
    );
  }

  const getRimText = (rimValue) => {
    if (!rimValue) return 'Não especificado';
    return rimValue.replace('aro-', '');
  };

  const handleDeleteStudent = async () => {
    const success = await deleteStudent(student.id);
    if (success) {
      onNavigate('dashboard');
    }
  };

  const handleDeleteEvaluation = async (id) => {
    const success = await deleteEvaluation(id);
    if (success) {
      loadData();
    }
  };

  const handleDeleteLog = async (id) => {
    const success = await deleteClassLog(id);
    if (success) {
      loadData();
    }
  };

  const handleViewEvaluation = (evaluation) => {
    setSelectedEvaluation(evaluation);
    setIsEvaluationModalOpen(true);
  };

  return (
    <div className="min-h-screen p-6">
      <div className="w-full max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex justify-between items-center mb-4">
            <Button
              onClick={() => onNavigate('dashboard')}
              variant="ghost"
              className="text-white hover:bg-white/10"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Voltar para o Dashboard
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="sm">
                  <Trash2 className="mr-2 h-4 w-4" />
                  Excluir Aluno
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Você tem certeza?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Essa ação não pode ser desfeita. Isso excluirá permanentemente o aluno e todos os seus dados, incluindo avaliações e relatos de evolução.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDeleteStudent}>Continuar</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>

          <Card className="bg-transparent border-white/20 text-center">
            <CardHeader>
              <div className="w-24 h-24 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 mx-auto flex items-center justify-center mb-4 border-4 border-white/20">
                <User className="w-12 h-12 text-white" />
              </div>
              <CardTitle className="text-3xl text-white">
                {student.student_name}
              </CardTitle>
              <p className="text-purple-300">
                {student.age} anos | {student.location} | Aro {getRimText(student.bike_rim)}
              </p>
            </CardHeader>
            <CardContent className="flex flex-col md:flex-row gap-4 justify-center">
              <Button
                onClick={() => onNavigate('evaluation', student)}
                size="lg"
                className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white text-lg py-6"
              >
                <ClipboardList className="mr-2 h-6 w-6" />
                Nova Avaliação
              </Button>
              <Button
                onClick={() => onNavigate('evolution', student)}
                size="lg"
                className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white text-lg py-6"
              >
                <TrendingUp className="mr-2 h-6 w-6" />
                Adicionar Evolução
              </Button>
            </CardContent>
          </Card>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 mt-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="bg-transparent border-white/20 h-full">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <BarChart2 className="mr-2 h-5 w-5" />
                  Histórico de Avaliações
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center items-center py-8">
                    <Loader2 className="h-8 w-8 text-white animate-spin" />
                  </div>
                ) : evaluations.length > 0 ? (
                  <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
                    {evaluations.map(ev => (
                      <div key={ev.id} className="p-4 rounded-lg bg-white/5 border border-white/10 group">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-semibold text-white">
                              {new Date(ev.date).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}
                            </p>
                            <p className="text-sm text-white/70">
                              Pontuação: {ev.total_score}/21
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => handleViewEvaluation(ev)}
                              className="bg-white text-blue-900 hover:bg-white/90 font-bold flex items-center shadow-md border-none"
                            >
                              <Eye className="mr-2 h-4 w-4" />
                              Ver detalhes
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-9 w-9 text-white hover:bg-white/10">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Excluir Avaliação?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Esta ação é permanente. Tem certeza que deseja excluir esta avaliação?
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleDeleteEvaluation(ev.id)}>Excluir</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </div>
                        <p className="text-white/90 mt-1">{ev.result_interpretation}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-white/60 py-8">
                    Nenhuma avaliação encontrada.
                  </p>
                )}
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card className="bg-transparent border-white/20 h-full">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <BookOpen className="mr-2 h-5 w-5" />
                  Histórico de Evolução
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center items-center py-8">
                    <Loader2 className="h-8 w-8 text-white animate-spin" />
                  </div>
                ) : evolutionLogs.length > 0 ? (
                  <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
                    {evolutionLogs.map(log => (
                      <div key={log.id} className="p-4 rounded-lg bg-white/5 border border-white/10 group">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-semibold text-white">
                              {new Date(log.date).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}
                            </p>
                            <p className="text-sm text-white/70">Instrutor: {log.instructor}</p>
                          </div>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Excluir Relato?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Esta ação é permanente. Tem certeza que deseja excluir este relato de evolução?
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleDeleteLog(log.id)}>Excluir</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                        <p className="text-white/90 mt-1">{log.report}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-white/60 py-8">
                    Nenhum relato de evolução encontrado.
                  </p>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
      <EvaluationDetailsModal
        isOpen={isEvaluationModalOpen}
        onClose={() => setIsEvaluationModalOpen(false)}
        evaluation={selectedEvaluation}
      />
    </div>
  );
};

export default StudentDashboard;